import { useEffect, useState, useRef } from "react";
import { ActionSheet, Button, Ellipsis, Picker, TabBar } from "antd-mobile";
import {
  useLocation,
  useRoutes,
  useNavigate,
  Navigate,
} from "react-router-dom";
import {
  CalendarOutlined,
  HistoryOutlined,
  HomeOutlined,
  PushpinOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "../../index.css";
// import { CheckOutline } from 'antd-mobile-icons'
import Appointments from "../../pages/Appointments/Appointments";
import enUS from "antd-mobile/es/locales/en-US";
import { ConfigProvider } from "antd-mobile";
import { getGeneralInfo } from "../../redux/actions/generalInfo/generalInfo";
import { useDispatch, useSelector } from "react-redux";
import { MessageError } from "../../component/Common/MessageError/MessageError";
import Loading from "../../component/Common/Loading/Loading";
import AppointmentDetail from "../../pages/AppointmentDetail/AppointmentDetail";
import CalendarAppointments from "../../pages/CalendarAppointments/CalendarAppointments";
import Sales from "../../pages/History/Sales/Sales";
import dayjs from "dayjs";
import Tips from "../../pages/History/Tips/Tips";
import queryString from "query-string";
import Profile from "../../pages/Profile/Profile";
import Error404 from "../../pages/Error/Error404";
import API from "../../utils/API";
import {
  changeBusiness,
  logout,
  updatePermissions,
} from "../../redux/actions/authenticate/authenticate";
import ChangePassword from "../../pages/ChangePassword/ChangePassword";
import BlockTime from "../../pages/BlockTime/BlockTime";
import CreateEditAppointment from "../../pages/CreateEditAppointment/CreateEditAppointment";
import decodeHtmlEntities from "../../utils/UnexcapeString/UnexcapeString";
import StaffNotifications from "../../layouts/Navigation/StaffNotifications";
import StaffNotificationDrawer from "../../pages/StaffNotification/StaffNotificationPage";
import { unregisterPushSubscription } from "./usePushNotifications";
import Cookies from "js-cookie";
import { safeParse } from "../../utils/utility";

const MenuContainer = () => {
  const dispatch = useDispatch();
  const { error, message, loading, staff } = useSelector(
    (state) => state.generalInfo,
  );
  const {
    error: authError,
    message: authMessage,
    loading: authLoading,
  } = useSelector((state) => state.authenticate);
  const { user } = useSelector((state) => state.authenticate);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [historyVisible, setHistoryVisible] = useState(false);
  const [accountVisible, setAccountVisible] = useState(false);
  const [historyFromTo, setHistoryFromTo] = useState({
    from: dayjs().format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
  });
  const [locationVisible, setLocationVisible] = useState(false);
  const { authenticated, isRequiredChangePassword, permissions } = user;
  const prevAuthenticated = useRef(authenticated);

  const hasHistory =
    permissions.includes("Sales") || permissions.includes("Tips");
  const hasAppointment = permissions.includes("Appointment");

  useEffect(() => {
    const getStaffAccount = async () => {
      try {
        const res = await API.get("/api/profile");
        const staff = res.data?.account;
        if (Array.isArray(staff?.permissions)) {
          const user = safeParse("user");
          if (user) {
            user.permissions = staff.permissions;
            localStorage.setItem("user", JSON.stringify(user));
          }
          dispatch(updatePermissions(staff.permissions));
        }
      } catch (error) {
        console.log("Cannot get staff account details");
      }
    };
    getStaffAccount();
  }, [dispatch]);

  useEffect(() => {
    const { historyFrom, historyTo } = queryString.parse(location.search);
    if (historyFrom && historyTo) {
      setHistoryFromTo({ from: historyFrom, to: historyTo });
    }
  }, [location.search]);

  useEffect(() => {
    // only redirect when authentication state changes from false to true
    if (!prevAuthenticated.current && authenticated) {
      navigate(isRequiredChangePassword ? "/account/change-password" : "/");
    }
    prevAuthenticated.current = authenticated;
  }, [authenticated, isRequiredChangePassword, navigate]);

  const setRouteActive = (value) => {
    const routerWithChilds = ["/history", "/account"];
    if (routerWithChilds.includes(value)) {
      if (value === "/history") {
        setHistoryVisible(true);
      } else if (value === "/account") {
        setAccountVisible(true);
      }
    } else {
      navigate(value);
    }
  };

  const elements = [
    hasAppointment
      ? {
          path: "/",
          element: <Appointments />,
          icon: (
            <PushpinOutlined
              style={
                pathname === "/"
                  ? { color: "#1677ff", fontWeight: "bold" }
                  : null
              }
            />
          ),
          title: "Appointments",
        }
      : {
          path: "/",
          element: <Navigate to="/account/profile" replace />,
          noNavigation: true,
        },
    hasAppointment && {
      path: "/calendar",
      element: <CalendarAppointments />,
      icon: (
        <CalendarOutlined
          style={
            pathname === "/calendar"
              ? { color: "#1677ff", fontWeight: "bold" }
              : null
          }
        />
      ),
      title: "Calendar",
    },
    hasHistory && {
      path: "/history",
      icon: (
        <HistoryOutlined
          style={
            pathname.includes("/history")
              ? { color: "#1677ff", fontWeight: "bold" }
              : null
          }
        />
      ),
      title: "History",
      children: [
        {
          text: (
            <div
              style={
                pathname === `/history/sales`
                  ? { color: "#1677ff", fontWeight: "bold" }
                  : null
              }
            >
              Sales {pathname === "/history/sales"}
            </div>
          ),
          key: "Sales",
          onClick: () => {
            navigate(
              `/history/sales?historyFrom=${historyFromTo.from}&historyTo=${historyFromTo.to}`,
            );
            setHistoryVisible(false);
          },
          path: "sales",
          element: <Sales historyFromTo={historyFromTo} />,
          disabled: !permissions.includes("Sales"),
        },

        {
          text: (
            <div
              style={
                pathname === `/history/tips`
                  ? { color: "#1677ff", fontWeight: "bold" }
                  : null
              }
            >
              Tips {pathname === "/history/tips"}
            </div>
          ),
          key: "Tips",
          onClick: () => {
            navigate(
              `/history/tips?historyFrom=${historyFromTo.from}&historyTo=${historyFromTo.to}`,
            );
            setHistoryVisible(false);
          },
          path: "tips",
          element: <Tips historyFromTo={historyFromTo} />,
          disabled: !permissions.includes("Tips"),
        },
      ],
    },
    {
      path: "/account",
      icon: (
        <UserOutlined
          style={
            pathname.includes("/account")
              ? { color: "#1677ff", fontWeight: "bold" }
              : null
          }
        />
      ),
      title: "Account",
      children: [
        {
          text: (
            <div
              style={
                pathname === "/account/profile"
                  ? { color: "#1677ff", fontWeight: "bold" }
                  : null
              }
            >
              Profile {pathname === "/account/profile"}
            </div>
          ),
          key: "Profile",
          onClick: () => {
            navigate("/account/profile");
            setAccountVisible(false);
          },
          path: "profile",
          element: <Profile />,
          disabled: isRequiredChangePassword,
        },
        {
          text: (
            <div
              style={
                pathname === "/account/change-password"
                  ? { color: "#1677ff", fontWeight: "bold" }
                  : null
              }
            >
              Change password {pathname === "/account/change-password"}
            </div>
          ),
          key: "ChangePassword",
          onClick: () => {
            navigate("/account/change-password");
            setAccountVisible(false);
          },
          path: "change-password",
          element: <ChangePassword />,
        },
        {
          text: "Logout",
          key: "Logout",
          danger: true,
          onClick: async () => {
            await unregisterPushSubscription();
            dispatch(logout());
          },
        },
      ],
    },
    hasAppointment && {
      path: "/appointment",
      element: <AppointmentDetail />,
      noNavigation: true,
    },
    {
      path: "/block-time",
      element: <BlockTime />,
      noNavigation: true,
    },
    {
      path: "/appointment/create",
      element: <CreateEditAppointment />,
      noNavigation: true,
    },
    hasAppointment && {
      path: "/notifications",
      element: <StaffNotificationDrawer />,
      noNavigation: true,
    },
    {
      path: "*",
      element: <Error404 />,
      noNavigation: true,
    },
  ];

  const routes = useRoutes(elements);

  useEffect(() => {
    dispatch(getGeneralInfo());
  }, [dispatch]);

  useEffect(() => {
    if (message || error) {
      MessageError(message, error);
    }
    if (authMessage || authError) {
      MessageError(authMessage, authError);
    }
  }, [error, message, authError, authMessage]);

  const onBusinessChange = async (value) => {
    await dispatch(changeBusiness(value[0]));
    // await reregisterPushSubscription();
    // navigate("/");  // single navigation after everything is done
  };

  if (loading || authLoading) {
    return <Loading />;
  }

  const businesses = [
    user?.businesses?.map((business) => ({
      label: (
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          <div>
            <Ellipsis
              direction="end"
              content={decodeHtmlEntities(business.name)}
            />
          </div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            <Ellipsis
              direction="end"
              content={decodeHtmlEntities(business.address)}
            />
          </div>
        </div>
      ),
      value: business._id,
    })),
  ];
  const raw = Cookies.get("business") || safeParse("business");
  const businessId = typeof raw === "string" ? raw.replace(/"/g, "") : "";

  // const businessName = decodeHtmlEntities(
  //     user?.businesses?.find((b) => b._id === businessId)?.name || ""
  // );

  // add this before the return statement
  if (isRequiredChangePassword && pathname !== "/account/change-password") {
    navigate("/account/change-password");
    return null;
  }

  return (
    <ConfigProvider locale={enUS}>
      <div className="layout" style={{ minHeight: window.innerHeight }}>
        <div className="top-nav">
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 10px",
              height: "45px",
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: 18 }}>
              {staff?.name}{" "}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {hasAppointment && (
                <div style={{ marginRight: "30px" }}>
                  <StaffNotifications />
                </div>
              )}

              <Button
                color="primary"
                style={{ marginLeft: "10px" }}
                onClick={() => setLocationVisible(true)}
              >
                <HomeOutlined style={{ fontSize: "1.2em" }} />
              </Button>
            </div>

            <Picker
              style={{ "--item-height": "60px" }}
              columns={businesses}
              visible={locationVisible}
              onClose={() => {
                setLocationVisible(false);
              }}
              value={businessId ? [businessId] : []}
              onConfirm={(v) => {
                onBusinessChange(v);
              }}
            />
          </div>
        </div>

        <div className="contain">{routes}</div>
        <div className="bottom-nav">
          <TabBar
            activeKey={null}
            onChange={(value) => setRouteActive(value)}
            safeArea={true}
          >
            {elements.map((item) =>
              item.noNavigation ? null : (
                <TabBar.Item
                  key={item.path}
                  icon={item.icon}
                  title={item.title}
                  style={
                    isRequiredChangePassword && item.title !== "Account"
                      ? { opacity: 0.3, pointerEvents: "none" }
                      : {}
                  }
                />
              ),
            )}
          </TabBar>
        </div>
        {hasHistory && (
          <ActionSheet
            visible={historyVisible}
            actions={elements
              .find((item) => item.path === "/history")
              .children.filter((item) => item.disabled === false)}
            onClose={() => setHistoryVisible(false)}
          />
        )}
        <ActionSheet
          visible={accountVisible}
          actions={elements.find((item) => item.path === "/account").children}
          onClose={() => setAccountVisible(false)}
        />
      </div>
    </ConfigProvider>
  );
};

export default MenuContainer;
